import { Style } from '@dicebear/core'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { useAvatarOptions, useAvatarStyle as useAvatarStyleQuery } from '@/api/client'
import type { AvatarEngine, AvatarStyleDefinition } from '@/api/model'

export const WEB_DICEBEAR_ENGINE = { package: 'dicebear-core', version: '10.3.0' } as const

// Query responses are immutable and retain their definition identity across consumers.
// Weak keys allow retired artwork to be collected when it leaves the query cache.
const stylesByDefinition = new WeakMap<AvatarStyleDefinition, Style>()

function sharedStyle(definition: AvatarStyleDefinition) {
  let style = stylesByDefinition.get(definition)
  if (!style) {
    style = new Style(definition)
    stylesByDefinition.set(definition, style)
  }
  return style
}

export function compatibleAvatarPublication(
  styleHash: string | undefined,
  catalogHash: string | undefined,
  engine: AvatarEngine | undefined,
) {
  return Boolean(
    styleHash &&
    styleHash === catalogHash &&
    engine?.package === WEB_DICEBEAR_ENGINE.package &&
    engine.version === WEB_DICEBEAR_ENGINE.version,
  )
}

/**
 * The avatar artwork, ready to draw with (`GET /api/v1/avatars/style/`).
 *
 * Returns a constructed `Style`, because building one is not free — the constructor
 * runs the definition through a JSON Schema validator and deep-clones it — and a creator
 * re-renders on every click. All portraits and creators sharing the query definition
 * reuse one `Style`, including when they unmount and mount again.
 *
 * The artwork and option overlay can rotate without a deploy. Both are revalidated as
 * one logical catalogue and are exposed only when their `artwork_hash` values agree.
 *
 * `engine` reports which DiceBear the definition was authored against
 * (`dicebear-core`, currently 10.3.0). Our `@dicebear/core` is pinned to the same
 * version in package.json — that pairing is what makes a local preview byte-identical
 * to what the backend will render and serve, so a mismatch is a real defect and worth
 * surfacing rather than rendering something subtly different.
 */
export function useAvatarStyle(enabled = true) {
  const querySettings = {
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: 'always' as const,
  }
  const styleQuery = useAvatarStyleQuery({ query: querySettings })
  const optionsQuery = useAvatarOptions({ query: querySettings })
  const refetchStyle = styleQuery.refetch
  const refetchOptions = optionsQuery.refetch
  const document = styleQuery.data?.status === 200 ? styleQuery.data.data : undefined
  const catalog = optionsQuery.data?.status === 200 ? optionsQuery.data.data : undefined

  const catalogHash =
    catalog && 'artwork_hash' in catalog && typeof catalog.artwork_hash === 'string'
      ? catalog.artwork_hash
      : undefined
  const hashesAgree = Boolean(document && catalog && document.artwork_hash === catalogHash)
  const publicationCompatible = compatibleAvatarPublication(
    document?.artwork_hash,
    catalogHash,
    document?.engine,
  )

  const definition = document?.definition
  const style = useMemo(
    () => (definition && publicationCompatible ? sharedStyle(definition) : undefined),
    [definition, publicationCompatible],
  )

  // A rotation may be observed by one endpoint a moment before the other. Retry the
  // mismatching pair once; rendering stays closed until a coherent catalogue arrives.
  const retriedMismatch = useRef<string | null>(null)
  useEffect(() => {
    if (!document || !catalog || hashesAgree) {
      retriedMismatch.current = null
      return
    }
    const mismatch = `${document.artwork_hash}:${catalogHash ?? 'missing'}`
    if (retriedMismatch.current === mismatch) return
    retriedMismatch.current = mismatch
    void Promise.all([refetchStyle(), refetchOptions()])
  }, [catalog, catalogHash, document, hashesAgree, refetchOptions, refetchStyle])

  const refetch = useCallback(
    () => Promise.all([refetchStyle(), refetchOptions()]),
    [refetchOptions, refetchStyle],
  )

  return {
    style,
    catalog: hashesAgree ? catalog : undefined,
    engine: document?.engine,
    artworkHash: document?.artwork_hash,
    isPending: styleQuery.isPending || optionsQuery.isPending,
    isError:
      (!styleQuery.isPending && !document) ||
      (!optionsQuery.isPending && !catalog) ||
      Boolean(document && !publicationCompatible),
    isAligned: publicationCompatible,
    refetch,
  }
}
